"use client";

import { Trans } from "@lingui/react/macro";
import { type ReactNode, useId } from "react";
import { DisplaySettingsSwitchRow } from "@/app/[lang]/[workspaceId]/_components/display-settings/display-settings-items";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { MemoDisplaySettings } from "./memo-events";

/**
 * chart 上の user memo 表示設定を編集する switch 群を描画します。
 * side panel と Dashboard menu で同じ localStorage-backed 設定を操作できるよう、表示だけを共有します。
 */
export function MemoDisplaySettingsFields({
  settings,
  onChange,
  className,
  variant = "default",
}: {
  settings: MemoDisplaySettings;
  onChange: (settings: MemoDisplaySettings) => void;
  className?: string;
  variant?: "default" | "display-settings";
}) {
  const SwitchRow =
    variant === "display-settings"
      ? DisplaySettingsSwitchRow
      : MemoDisplaySettingsSwitch;

  return (
    <div
      className={cn(
        variant === "default" ? "space-y-3" : "space-y-0.5",
        className,
      )}
    >
      <SwitchRow
        label={<Trans>Hide period bars</Trans>}
        checked={settings.hidePeriodBars}
        onCheckedChange={(checked) =>
          onChange({ ...settings, hidePeriodBars: checked })
        }
      />
      <SwitchRow
        label={<Trans>Hide memos</Trans>}
        checked={settings.hideMemos}
        onCheckedChange={(checked) =>
          onChange({ ...settings, hideMemos: checked })
        }
      />
      <SwitchRow
        label={<Trans>Hide memos in metric tooltip</Trans>}
        checked={settings.hideMetricTooltipMemos}
        onCheckedChange={(checked) =>
          onChange({ ...settings, hideMetricTooltipMemos: checked })
        }
      />
    </div>
  );
}

/**
 * memo 表示設定の 1 行分を label と switch の関連付きで描画します。
 * Radix Switch を外部 label から操作できるよう id を共有します。
 */
function MemoDisplaySettingsSwitch({
  label,
  checked,
  onCheckedChange,
}: {
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const switchId = useId();

  return (
    <div className="flex items-center justify-between gap-4">
      <label
        htmlFor={switchId}
        className="cursor-pointer whitespace-nowrap text-muted-foreground text-sm"
      >
        {label}
      </label>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}
